import useIdentifierAssociations from '@cobliteam/hooks/src/useEntity/useIdentifierAssociations';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { useLanguage } from '@cobliteam/i18n';
import { usePermissions } from '@cobliteam/permissions';
import type React from 'react';
import { createContext, useContext } from 'react';
import type { Environment, RouteDef, RuntimeFlags, SidebarDef } from '../../dashboardDef';
import dashboardDefFile from '../../dashboardDef.json';
import LoadingPage from '../../pages/LoadingPage';
import getEnvironment from './helpers/getEnvironment';
import getFleetSize from './helpers/getFleetSize';
import getSidebar from './helpers/getSidebar';

// Define what the context provides
interface DashboardContextValue {
  dashboardDef: RouteDef[];
  sidebar: SidebarDef;
  runtimeFlags: RuntimeFlags;
  environment: Environment;
}

// Create the context
const DashboardContext = createContext<DashboardContextValue | undefined>(undefined);

// Provider props
interface SimpleProviderProps {
  children: React.ReactNode;
}

// Provider component
const DashboardProvider = ({ children }: SimpleProviderProps) => {
  const { isAccessAllowed } = usePermissions();
  const { currentLanguage } = useLanguage();
  const featureFlags = useFeatureFlags();
  const { session, user, isSafetyCoacher, hasNoValidGroups, hasOnlyGroupsWithNoVehicles, noValidGroupOrVehicles, hasGroupsRestriction, useGetUserData } = useUser();
  const { data: userData } = useGetUserData(session?.user.id, !!session);
  const fleetSize = getFleetSize(userData);
  const dashboardDef = dashboardDefFile as RouteDef[];

  const { getIdentificationTokens } = useIdentifierAssociations.useIdentificationToken();
  // Must be called before any conditional return to comply with React hooks rules
  const { data: identifiers } = getIdentificationTokens();

  if (!user) return <LoadingPage message='Carregando informações do usuário...' />;
  if (!userData) return <LoadingPage message='Carregando informações do usuário...' />;

  // Flags that are used at runtime to build the sidebar
  const runtimeFlags: RuntimeFlags = {
    hasCameraFlag: featureFlags.enforceCameraFeatures || userData?.hasCameraFlag,
    hasRedeCan: userData?.hasRedeCan,
    hasGroupsRestriction,
    hasNoValidGroups,
    hasOnlyGroupsWithNoVehicles,
    noValidGroupOrVehicles,
    fleetSize,
    isSafetyCoacher,
    // Default true while loading — hide only once confirmed empty
    hasIdentifiers: identifiers == null ? true : identifiers.length > 0,
  };

  const environment = getEnvironment();

  const sidebar = getSidebar({ dashboardDef, runtimeFlags, featureFlags, isAccessAllowed, language: currentLanguage });

  const initialValues = { dashboardDef, sidebar, runtimeFlags, environment };

  return <DashboardContext.Provider value={initialValues}>{children}</DashboardContext.Provider>;
};

// Custom hook
export const useDashboard = () => {
  const context = useContext(DashboardContext);

  if (!context) throw new Error('useDashboard must be used within DashboardProvider');

  return context;
};

export default DashboardProvider;
